"use client"

import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Modal,
    ModalOverlay,
    ModalContent,
    Button,
    useDisclosure,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    VStack,
    Grid,
    GridItem,
    FormControl,
    FormLabel,
    ModalFooter,
    Text,
    HStack,
    Tooltip,
    IconButton,
    Select,
    Textarea,
    Box,
    useToast,
    Badge,
} from "@chakra-ui/react"
import { DeleteIcon, EditIcon, ViewIcon } from "@chakra-ui/icons"
import {
    selectContactUs,
    fetchContactUsAsync,
    updateContactUsAsync,
    deleteContactUsAsync,
} from "../../ContactUs/ContactUsSlice"

const ContactUsAdmin = () => {
    const dispatch = useDispatch()
    const contactusdata = useSelector(selectContactUs)
    const toast = useToast()

    // Modal disclosures
    const { isOpen: isEditModalOpen, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure()

    const { isOpen: isDeleteModalOpen, onOpen: onOpenDeleteModal, onClose: onCloseDeleteModal } = useDisclosure()

    const { isOpen: isViewModalOpen, onOpen: onOpenViewModal, onClose: onCloseViewModal } = useDisclosure()

    // State management
    const [editingContact, setEditingContact] = useState(null)
    const [deletingContact, setDeletingContact] = useState(null)
    const [viewingContact, setViewingContact] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [error, setError] = useState(null)

    const contactsPerPage = 15
    const totalPages = Math.ceil((contactusdata?.length || 0) / contactsPerPage)

    // Pagination handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1)
        }
    }

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1)
        }
    }

    // Fetch contact us data
    useEffect(() => {
        dispatch(fetchContactUsAsync())
            .then(() => setIsLoading(false))
            .catch(() => setIsLoading(false))
    }, [dispatch])

    // Handle input changes for editing contact
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setEditingContact({
            ...editingContact,
            [name]: value,
        })
    }

    // Open edit modal
    const openEditModal = (contact) => {
        setEditingContact({
            uuid: contact.uuid,
            status: contact.status,
            reply_description: contact.reply_description || "",
            reply_status: contact.reply_status,
        })
        onOpenEditModal()
    }

    // Save edited contact
    const handleSave = () => {
        if (!editingContact.uuid) {
            setError("Contact UUID is required")
            return
        }

        const updateData = {
            uuid: editingContact.uuid,
            status: editingContact.status,
            reply_description: editingContact.reply_description,
            reply_status: editingContact.reply_status,
        }

        dispatch(updateContactUsAsync(updateData))
            .then(() => {
                setEditingContact(null)
                setError(null)
                onCloseEditModal()

                toast({
                    title: "Contact updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                })

                // Refresh the contact us list
                dispatch(fetchContactUsAsync())
            })
            .catch((error) => {
                setError("Failed to update contact: " + (error.message || "Unknown error"))
            })
    }

    // Open delete confirmation modal
    const openDeleteModal = (contact) => {
        setDeletingContact(contact)
        onOpenDeleteModal()
    }

    // Delete contact
    const handleDelete = () => {
        if (!deletingContact?.uuid) {
            toast({
                title: "Error",
                description: "Contact UUID is required for deletion",
                status: "error",
                duration: 3000,
                isClosable: true,
            })
            return
        }

        dispatch(deleteContactUsAsync(deletingContact.uuid))
            .then(() => {
                setDeletingContact(null)
                onCloseDeleteModal()

                toast({
                    title: "Contact deleted successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                })

                // Refresh the contact us list
                dispatch(fetchContactUsAsync())
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: "Failed to delete contact: " + (error.message || "Unknown error"),
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                })
            })
    }

    // Open view modal
    const handleView = (contact) => {
        setViewingContact(contact)
        onOpenViewModal()
    }

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case "resolved":
                return "green"
            case "pending":
                return "orange"
            case "in_progress":
                return "blue"
            default:
                return "gray"
        }
    }

    // Get reply status color
    const getReplyStatusColor = (replyStatus) => {
        switch (replyStatus) {
            case "replied":
                return "green"
            case "not_replied":
                return "red"
            default:
                return "gray"
        }
    }

    // Format date
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    // Get current page data
    const startIndex = (currentPage - 1) * contactsPerPage
    const endIndex = startIndex + contactsPerPage
    const currentContacts = contactusdata?.slice(startIndex, endIndex) || []

    return (
        <div className="h-screen bg-adminbg contentAdmin p-4" style={{ fontFamily: "Inter, sans-serif" }}>
            <div className="w-full my-5 bg-white rounded-xl">
                <div className="flex justify-between">
                    <p className="text-xl text-black font-semibold px-3 py-7">Contact Us Management</p>
                </div>

                <TableContainer>
                    <Table size="sm">
                        <Thead className="bg-gray-200">
                            <Tr>
                                <Th className="w-1/12">ID</Th>
                                <Th className="w-1/8">User Type</Th>
                                <Th className="w-1/8">Type</Th>
                                <Th className="w-1/6">Name</Th>
                                <Th className="w-1/6">Email</Th>
                                <Th className="w-1/8">Mobile</Th>
                                <Th className="w-1/6">Subject</Th>
                                <Th className="w-1/8">Status</Th>
                                <Th className="w-1/8">Reply Status</Th>
                                <Th className="w-1/6">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {currentContacts?.length > 0 ? (
                                currentContacts.map((contact, index) => (
                                    <Tr key={contact.id}>
                                        <Td>{index + 1}</Td>
                                        <Td>
                                            <Badge colorScheme={contact.user_type === "candidate" ? "blue" : "purple"} variant="subtle">
                                                {contact.user_type}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={contact.type === "sales" ? "green" : "orange"} variant="subtle">
                                                {contact.type}
                                            </Badge>
                                        </Td>
                                        <Td>{contact.name}</Td>
                                        <Td>{contact.email}</Td>
                                        <Td>{contact.mobile}</Td>
                                        <Td>
                                            {contact.subject?.length > 20 ? `${contact.subject?.substring(0, 20)}...` : contact.subject}
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={getStatusColor(contact.status)} variant="subtle">
                                                {contact.status}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={getReplyStatusColor(contact.reply_status)} variant="subtle">
                                                {contact.reply_status}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Tooltip label="View Contact" placement="top">
                                                    <IconButton
                                                        icon={<ViewIcon />}
                                                        colorScheme="teal"
                                                        size="sm"
                                                        onClick={() => handleView(contact)}
                                                        aria-label="View contact"
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Reply Contact" placement="top">
                                                    <IconButton
                                                        icon={<EditIcon />}
                                                        colorScheme="blue"
                                                        size="sm"
                                                        onClick={() => openEditModal(contact)}
                                                        aria-label="Reply contact"
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Delete Contact" placement="top">
                                                    <IconButton
                                                        icon={<DeleteIcon />}
                                                        colorScheme="red"
                                                        size="sm"
                                                        onClick={() => openDeleteModal(contact)}
                                                        aria-label="Delete contact"
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan="10" className="text-center py-4 text-gray-500">
                                        {isLoading ? "Loading..." : "No contacts available"}
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <GridItem colSpan={2} display="flex" justifyContent="center">
                    <div className="flex w-full justify-evenly items-center mt-4 mb-4">
                        <button
                            className={`bg-gray-300 text-black px-4 py-2 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span>
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            className={`bg-blue-500 text-white px-4 py-2 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </GridItem>
            </div>

            {/* Edit Contact Modal */}
            <Modal isOpen={isEditModalOpen} onClose={onCloseEditModal} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                        Reply Contact
                    </ModalHeader>
                    <ModalBody py={6}>
                        <VStack spacing={6}>
                            <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                                <GridItem colSpan={1}>
                                    <FormControl isRequired>
                                        <FormLabel>Status</FormLabel>
                                        <Select name="status" value={editingContact?.status || ""} onChange={handleInputChange}>
                                            <option value="pending">Pending</option>
                                            <option value="closed">Closed</option>
                                            <option value="resolved">Resolved</option>
                                        </Select>
                                    </FormControl>
                                </GridItem>
                                <GridItem colSpan={1}>
                                    <FormControl isRequired>
                                        <FormLabel>Reply Status</FormLabel>
                                        <Select name="reply_status" value={editingContact?.reply_status || ""} onChange={handleInputChange}>
                                            <option value="not_replied">Not Replied</option>
                                            <option value="replied">Replied</option>
                                        </Select>
                                    </FormControl>
                                </GridItem>
                                <GridItem colSpan={2}>
                                    <FormControl>
                                        <FormLabel>Reply Description</FormLabel>
                                        <Textarea
                                            name="reply_description"
                                            placeholder="Enter reply description"
                                            value={editingContact?.reply_description || ""}
                                            onChange={handleInputChange}
                                            rows={4}
                                        />
                                    </FormControl>
                                </GridItem>
                            </Grid>
                            {error && <Text color="red.500">{error}</Text>}
                        </VStack>
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                        <Button colorScheme="blue" mr={3} onClick={handleSave}>
                            Save
                        </Button>
                        <Button onClick={onCloseEditModal}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal}>
                <ModalOverlay />
                <ModalContent maxW="2xl" borderRadius="lg" boxShadow="lg">
                    <ModalHeader fontSize="lg" fontWeight="bold" textAlign="center">
                        Confirm Deletion
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="md" textAlign="center">
                            Are you sure you want to delete the contact from{" "}
                            <Text as="span" fontWeight="semibold" color="red.500">
                                "{deletingContact?.name}"
                            </Text>
                            ?
                        </Text>
                    </ModalBody>
                    <ModalFooter display="flex" gap="2" justifyContent="center">
                        <Button colorScheme="red" w="40" onClick={handleDelete}>
                            Delete
                        </Button>
                        <Button variant="outline" colorScheme="red" w="40" onClick={onCloseDeleteModal}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* View Contact Modal */}
            <Modal isOpen={isViewModalOpen} isCentered onClose={onCloseViewModal} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
                        View Contact Details
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody py={6}>
                        {viewingContact && (
                            <VStack spacing={4} align="start">
                                <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                                    <Box>
                                        <Text fontWeight="bold">ID:</Text>
                                        <Text>{viewingContact.id}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">UUID:</Text>
                                        <Text fontSize="sm">{viewingContact.uuid}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">User Type:</Text>
                                        <Badge colorScheme={viewingContact.user_type === "candidate" ? "blue" : "purple"}>
                                            {viewingContact.user_type}
                                        </Badge>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Type:</Text>
                                        <Badge colorScheme={viewingContact.type === "sales" ? "green" : "orange"}>
                                            {viewingContact.type}
                                        </Badge>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Name:</Text>
                                        <Text>{viewingContact.name}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Email:</Text>
                                        <Text>{viewingContact.email}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Mobile:</Text>
                                        <Text>{viewingContact.mobile}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Status:</Text>
                                        <Badge colorScheme={getStatusColor(viewingContact.status)}>{viewingContact.status}</Badge>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Reply Status:</Text>
                                        <Badge colorScheme={getReplyStatusColor(viewingContact.reply_status)}>
                                            {viewingContact.reply_status}
                                        </Badge>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Created At:</Text>
                                        <Text fontSize="sm">{formatDate(viewingContact.created_at)}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontWeight="bold">Updated At:</Text>
                                        <Text fontSize="sm">{formatDate(viewingContact.updated_at)}</Text>
                                    </Box>
                                </Grid>
                                <Box width="100%">
                                    <Text fontWeight="bold">Subject:</Text>
                                    <Text>{viewingContact.subject}</Text>
                                </Box>
                                <Box width="100%">
                                    <Text fontWeight="bold">Description:</Text>
                                    <Text>{viewingContact.description}</Text>
                                </Box>
                                {viewingContact.reply_description && (
                                    <Box width="100%">
                                        <Text fontWeight="bold">Reply Description:</Text>
                                        <Text>{viewingContact.reply_description}</Text>
                                    </Box>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                        <Button onClick={onCloseViewModal}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}

export default ContactUsAdmin
